"use client";

import { useEffect } from "react";
import { useAuthStore } from "@/store";
import { getMe, isAuthenticated, clearToken } from "@/lib/api";

/**
 * Roda 1x quando o app monta. Responsável por:
 *   1. Validar o access token no localStorage chamando /auth/me
 *   2. Popular `useAuthStore.user` com a versão mais recente do backend
 *   3. Disparar o refresh automático (já implementado dentro do `request`
 *      em lib/api.ts) se o access estiver expirado mas o refresh cookie
 *      ainda for válido
 *   4. Limpar a sessão se nem o refresh resolver (cookie revogado, etc)
 *
 * O Zustand persist já reidrata `user` do localStorage instantaneamente
 * — então a UI não pisca de "deslogado" pra "logado" depois do refresh.
 * Este componente serve pra revalidar e detectar sessões inválidas em
 * background.
 */
export function AuthSync() {
  const setUser = useAuthStore((s) => s.setUser);
  const setHasHydrated = useAuthStore((s) => s.setHasHydrated);

  useEffect(() => {
    let alive = true;

    (async () => {
      // Sem token → marca hidratado e segue. Se havia user persistido
      // mas o token sumiu (logout em outra aba, limpar dados, etc),
      // limpa também.
      if (!isAuthenticated()) {
        if (alive) {
          setUser(null);
          setHasHydrated(true);
        }
        return;
      }

      const res = await getMe();
      if (!alive) return;

      if (res.success) {
        setUser(res.data.user);
      } else {
        // /auth/me com 401 → request() já tentou refresh + retry.
        // Se chegou aqui ainda em erro, sessão acabou de verdade.
        if (res.error.details?.status === 401) {
          clearToken();
          setUser(null);
        }
        // Erros de rede/timeout: mantém o user cached (Zustand persist)
        // pra evitar logout "fantasma" quando o usuário só está sem net.
      }
      setHasHydrated(true);
    })();

    return () => {
      alive = false;
    };
  }, [setUser, setHasHydrated]);

  return null;
}
